import { useCallback, useEffect, useRef, useState } from "react"
import { defaultBunkerPricesPayload } from "@/config/defaults"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { nowMs } from "@/utils/appNow"
import { useAccessContext } from "@/contexts/AccessContext"
import {
  createVessel,
  buildNewVoyage,
} from "@/services/shippingDbApi"
import { corePost } from "@/services/coreApi"
import { useRouteStore } from "@/features/route/state/RouteStore.jsx"
import { normalizeTreeMetadata } from "@/features/route/state/RouteAdapters.jsx"
import {
  createEmptyNormalizedState,
  normalizeImportedRoute,
  selectDraftRouteIds,
  selectNextDraftRouteId,
  selectRenderableRoute,
} from "@/features/route/state/index.jsx"

dayjs.extend(utc)

// Vessel position along the route now: the last waypoint at or before the
// current time, falling back to the first. Returns [lon, lat] or null.
function vesselPositionOnRoute(features) {
  const list = Array.isArray(features) ? features : []
  if (list.length === 0) return null
  const cutoffMs = nowMs()
  let current = list[0]
  for (const f of list) {
    const ts = f?.properties?.timestamp
    const ms = ts ? dayjs.utc(ts).valueOf() : NaN
    if (Number.isFinite(ms) && ms <= cutoffMs) current = f
  }
  const coords = current?.geometry?.coordinates
  return Array.isArray(coords) && coords.length === 2
    ? [coords[0], coords[1]]
    : null
}

export default function useRouteImportFlow(
  onReportedDataChanged,
  onError,
  onImportSaved,
  // The panel hosting the confirmation form (Fleet Explorer). Panels close
  // each other exclusively, so a pending import must cancel when its host
  // disappears — the preview would otherwise stay on the map unactionable.
  previewHostOpen = true,
  recenterToRoute = null,
) {
  const { accessContext, setAccessContext } = useAccessContext()
  const { state: routeStoreState, replaceRoute, upsertEntities } = useRouteStore()
  const [importDraft, setImportDraft] = useState(null)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [importSuccessTick, setImportSuccessTick] = useState(0)
  const [importSaving, setImportSaving] = useState(false)

  const storeStateRef = useRef(routeStoreState)
  storeStateRef.current = routeStoreState
  // Store slot of the map preview; lives until save/cancel.
  const previewRouteIdRef = useRef(null)

  // Sweeps every import slot, not just the tracked one: an on-map recalc
  // that lands after cancel can revive a cleared slot (ghost preview).
  const clearPreview = useCallback(() => {
    const state = storeStateRef.current
    const staleIds = new Set(
      selectDraftRouteIds(state).filter(
        (id) => state?.entities?.routesById?.[id]?.source === "import",
      ),
    )
    if (previewRouteIdRef.current != null) staleIds.add(previewRouteIdRef.current)
    staleIds.forEach((id) => replaceRoute(id, createEmptyNormalizedState()))
    previewRouteIdRef.current = null
  }, [replaceRoute])

  const handleImportDraft = useCallback(
    (draft) => {
      clearPreview()
      const routeId = selectNextDraftRouteId(storeStateRef.current)
      replaceRoute(routeId, normalizeImportedRoute(draft, { routeId }))
      previewRouteIdRef.current = routeId
      setImportDraft(draft)
      setImportDialogOpen(true)
      recenterToRoute?.(routeId)
    },
    [clearPreview, replaceRoute, recenterToRoute],
  )

  const handleImportCancel = useCallback(() => {
    clearPreview()
    setImportDialogOpen(false)
    setImportDraft(null)
  }, [clearPreview])

  useEffect(() => {
    if (previewHostOpen || !importDialogOpen) return
    handleImportCancel()
  }, [previewHostOpen, importDialogOpen, handleImportCancel])

  const handleImportConfirm = useCallback(
    async ({ draftRoute, routeName, vesselSelection, voyageSelection, schedule }) => {
      setImportSaving(true)
      try {
        // An RTZ file without times gets them from the entered departure and speed.
        const timedFile = draftRoute?.needsSchedule
          ? (
              await corePost(
                "/services/route-import-convert",
                { content: draftRoute.sourceText, ...schedule },
                accessContext,
                setAccessContext,
                ".core /services/route-import-convert error=",
              )
            ).file
          : null
        const file = timedFile ?? draftRoute?.sourceData ?? {}
        // Core imports the raw file; the (read-only, identical) preview only
        // feeds the voyage times and the recenter target.
        const previewFeatures =
          previewRouteIdRef.current == null
            ? []
            : selectRenderableRoute(
                storeStateRef.current,
                previewRouteIdRef.current,
              )?.points?.geojson?.features ?? []
        const routeFeatures =
          previewFeatures.length > 0
            ? previewFeatures
            : draftRoute?.geojson?.features ?? []
        const fileWps = file?.baseRoute?.wps ?? []

        const vesselName =
          vesselSelection?.mode === "existing"
            ? vesselSelection?.vessel?.name || ""
            : vesselSelection?.name || ""

        let vesselId =
          vesselSelection?.mode === "existing"
            ? vesselSelection?.vessel?.id ?? null
            : null

        if (!vesselId && vesselSelection?.mode === "new") {
          const createdVessel = await createVessel({
            accessContext,
            setAccessContext,
            customerId: accessContext?.payload?.customerId,
            accountId: accessContext?.payload?.accountId,
            name: vesselName || draftRoute.vesselName || "Imported Vessel",
            imo: "",
            active: true,
          })
          vesselId = createdVessel.id
          upsertEntities(normalizeTreeMetadata({ vessels: [createdVessel] }))
        }

        const existingVoyageId =
          voyageSelection?.mode === "existing"
            ? voyageSelection?.voyage?.id ?? null
            : null
        const isNewVoyage = !existingVoyageId && Boolean(vesselId) && voyageSelection?.mode === "new"
        if (!existingVoyageId && !isNewVoyage) throw new Error("Voyage selection is missing")

        // Edited previews own both voyage times; the raw file keeps its
        // departureTime preference otherwise.
        const newVoyage = isNewVoyage
          ? buildNewVoyage({
              name: voyageSelection?.name || routeName,
              features: routeFeatures,
              etd: timedFile
                ? timedFile.departureTime
                : previewFeatures.length > 0
                  ? previewFeatures[0]?.properties?.timestamp ?? draftRoute?.startTime
                  : draftRoute?.startTime,
              eta: timedFile
                ? fileWps.at(-1)?.calculatedTimestamp
                : routeFeatures.at(-1)?.properties?.timestamp ?? draftRoute?.endTime,
            })
          : undefined

        // Core owns the voyage-file format (units, profile mirroring,
        // json_request snapshot), analyses the route with current weather
        // and creates voyage, route and waypoints with rollback.
        const imported = await corePost(
          "/routes/import",
          {
            ...(isNewVoyage ? { newVoyage } : { voyageId: existingVoyageId }),
            vesselId: vesselId ?? undefined,
            name: routeName,
            state: "suggested",
            bunkerPrices: defaultBunkerPricesPayload(),
            file,
          },
          accessContext,
          setAccessContext,
          ".core /routes/import error=",
        )
        const voyageId = Number(imported?.voyage?.id)
        const voyageName =
          imported?.voyage?.name || voyageSelection?.voyage?.name || routeName
        const routeId = Number(imported?.route?.id)
        if (!Number.isFinite(routeId)) {
          throw new Error("Import failed: no route id returned")
        }

        clearPreview()
        setImportDialogOpen(false)
        setImportDraft(null)
        setImportSuccessTick((t) => t + 1)
        onReportedDataChanged?.(true)
        onImportSaved?.({
          routeId,
          vesselId,
          vesselName,
          voyageId,
          voyageName,
          routeName,
          routeType: "planned",
          state: isNewVoyage ? "active" : "suggested",
          voyageActivated: isNewVoyage,
          archivedVoyageName:
            imported?.archivedVoyageId == null
              ? null
              : (storeStateRef.current?.entities?.voyagesById?.[imported.archivedVoyageId]?.name ?? ""),
          timestamp: new Date().toISOString(),
          summary: imported?.route?.summary ?? null,
          analysed: imported?.analysed !== false,
          analysisError: imported?.analysisError ?? null,
          skippedWaypoints: Number(imported?.skippedWaypoints) || 0,
          recenterLonLat: vesselPositionOnRoute(routeFeatures),
        })
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)))
      } finally {
        setImportSaving(false)
      }
    },
    [accessContext, onError, onImportSaved, setAccessContext, onReportedDataChanged, clearPreview, upsertEntities]
  )

  return {
    importDraft,
    importDialogOpen,
    importSuccessTick,
    importSaving,
    handleImportDraft,
    handleImportCancel,
    handleImportConfirm,
  }
}
